import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, Transition } from 'framer-motion';
import { Users, Mail, Handshake, CheckCircle } from 'lucide-react';
import { isEqual } from 'lodash';
import SectionContainer from '../../components/shared/ui/SectionContainer';
import SectionHeader from '../../components/shared/ui/SectionHeader';
import { useSnapshotScope } from '../../contexts/SnapshotScope';
import { useSimpleSnapshot } from '../../hooks/useSimpleSnapshot';
import { useUser } from '../../contexts/UserContext';
import { logger } from '../../utils/logger';
import { getCurrentWeekKey, getWeekStartDateFromKey } from '../../utils/weekKeyUtils';
import { ALLOW_PAST_WEEK_EDITS } from '../../config/auth';

interface FunnelData {
  newCreators: number;
  inSequence: number;
  inNegotiation: number;
  activeCreators: number;
}

// Simple Counter Component - displays value directly (no animation)
// Removed animation to prevent pulse-like effect on mode switch
const AnimatedCounter: React.FC<{ value: number; duration?: number }> = ({ 
  value 
}) => {
  // Display value directly without animation
  return <>{value}</>;
};

type FunnelStageConfig = {
  key: keyof FunnelData;
  label: string;
  icon: typeof Users;
  gradient: [string, string];
  pulseRing: string;
  iconBg: string;
  iconAccent: string;
  labelColor: string; // Tailwind color class for label
};

const detectDarkTheme = () => {
  if (typeof window === 'undefined') return false;
  const root = document.documentElement;
  // ✅ FIX: Use [data-theme] attribute instead of .theme-dark class (unified theme system)
  const dataThemeDark = root.getAttribute('data-theme') === 'dark';
  if (dataThemeDark) return true;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

const createFunnelStages = (isDark: boolean, isViewerMode: boolean = false): FunnelStageConfig[] => {
  // Viewer mode: Vibrant colors in dark mode, softer in light mode
  if (isViewerMode) {
    if (isDark) {
      return [
        {
          key: 'newCreators',
          label: 'New Creators',
          icon: Users,
          gradient: ['var(--funnel-new-creators-start)', 'var(--funnel-new-creators-end)'],
          pulseRing: 'rgba(96, 165, 250, 0.40)', // Vibrant blue
          iconBg: 'rgba(59, 130, 246, 0.25)', // Vibrant icon background
          iconAccent: '#3b82f6', // Bright blue-500
          labelColor: 'text-emerald-300 dark:text-emerald-200' // Softer label color
        },
        {
          key: 'inSequence',
          label: 'In Sequence',
          icon: Mail,
          gradient: ['var(--funnel-in-sequence-start)', 'var(--funnel-in-sequence-end)'],
          pulseRing: 'rgba(251, 191, 36, 0.40)', // Vibrant amber
          iconBg: 'rgba(245, 158, 11, 0.25)', // Vibrant icon background
          iconAccent: '#f59e0b', // Bright amber-500
          labelColor: 'text-sky-300 dark:text-sky-200'
        },
        {
          key: 'inNegotiation',
          label: 'In Negotiation',
          icon: Handshake,
          gradient: ['var(--funnel-in-negotiation-start)', 'var(--funnel-in-negotiation-end)'],
          pulseRing: 'rgba(251, 146, 60, 0.40)', // Vibrant orange
          iconBg: 'rgba(249, 115, 22, 0.25)', // Vibrant icon background
          iconAccent: '#f97316', // Bright orange-500
          labelColor: 'text-amber-300 dark:text-amber-200'
        },
        {
          key: 'activeCreators',
          label: 'Active Creators',
          icon: CheckCircle,
          gradient: ['var(--funnel-active-creators-start)', 'var(--funnel-active-creators-end)'],
          pulseRing: 'rgba(52, 211, 153, 0.40)', // Vibrant green
          iconBg: 'rgba(16, 185, 129, 0.25)', // Vibrant icon background
          iconAccent: '#10b981', // Bright emerald-500
          labelColor: 'text-violet-300 dark:text-violet-200'
        }
      ];
    }
    // Light mode viewer
    return [
      {
        key: 'newCreators',
        label: 'New Creators',
        icon: Users,
        gradient: ['var(--funnel-new-creators-start)', 'var(--funnel-new-creators-end)'],
        pulseRing: 'rgba(59, 130, 246, 0.12)',
        iconBg: 'rgba(59, 130, 246, 0.08)',
        iconAccent: '#60a5fa',
        labelColor: 'text-emerald-600 dark:text-emerald-500'
      },
      {
        key: 'inSequence',
        label: 'In Sequence',
        icon: Mail,
        gradient: ['var(--funnel-in-sequence-start)', 'var(--funnel-in-sequence-end)'],
        pulseRing: 'rgba(245, 158, 11, 0.12)',
        iconBg: 'rgba(245, 158, 11, 0.08)',
        iconAccent: '#f59e0b',
        labelColor: 'text-sky-600 dark:text-sky-500'
      },
      {
        key: 'inNegotiation',
        label: 'In Negotiation',
        icon: Handshake,
        gradient: ['var(--funnel-in-negotiation-start)', 'var(--funnel-in-negotiation-end)'],
        pulseRing: 'rgba(249, 115, 22, 0.12)',
        iconBg: 'rgba(249, 115, 22, 0.08)',
        iconAccent: '#f97316',
        labelColor: 'text-amber-600 dark:text-amber-500'
      },
      {
        key: 'activeCreators',
        label: 'Active Creators',
        icon: CheckCircle,
        gradient: ['var(--funnel-active-creators-start)', 'var(--funnel-active-creators-end)'],
        pulseRing: 'rgba(16, 185, 129, 0.12)',
        iconBg: 'rgba(16, 185, 129, 0.08)',
        iconAccent: '#10b981',
        labelColor: 'text-violet-600 dark:text-violet-500'
      }
    ];
  }

  // Admin/edit mode: Vibrant colors for dark mode
  if (isDark) {
    return [
      {
        key: 'newCreators',
        label: 'New Creators',
        icon: Users,
        gradient: ['var(--funnel-new-creators-start)', 'var(--funnel-new-creators-end)'],
        pulseRing: 'rgba(96, 165, 250, 0.40)', // More vibrant blue
        iconBg: 'rgba(59, 130, 246, 0.25)', // More vibrant icon background
        iconAccent: '#3b82f6', // Bright blue-500
        labelColor: 'text-green-400 dark:text-green-300' // Green-ish tone for New Creators - improved contrast
      },
      {
        key: 'inSequence',
        label: 'In Sequence',
        icon: Mail,
        gradient: ['var(--funnel-in-sequence-start)', 'var(--funnel-in-sequence-end)'],
        pulseRing: 'rgba(251, 191, 36, 0.40)', // More vibrant amber
        iconBg: 'rgba(245, 158, 11, 0.25)', // More vibrant icon background
        iconAccent: '#f59e0b', // Bright amber-500
        labelColor: 'text-blue-400 dark:text-blue-300' // Blue tone for In Sequence - improved contrast
      },
      {
        key: 'inNegotiation',
        label: 'In Negotiation',
        icon: Handshake,
        gradient: ['var(--funnel-in-negotiation-start)', 'var(--funnel-in-negotiation-end)'],
        pulseRing: 'rgba(251, 146, 60, 0.40)', // More vibrant orange
        iconBg: 'rgba(249, 115, 22, 0.25)', // More vibrant icon background
        iconAccent: '#f97316', // Bright orange-500
        labelColor: 'text-amber-400 dark:text-amber-300' // Yellow/amber tone for In Negotiation - improved contrast
      },
      {
        key: 'activeCreators',
        label: 'Active Creators',
        icon: CheckCircle,
        gradient: ['var(--funnel-active-creators-start)', 'var(--funnel-active-creators-end)'],
        pulseRing: 'rgba(52, 211, 153, 0.40)', // More vibrant green
        iconBg: 'rgba(16, 185, 129, 0.25)', // More vibrant icon background
        iconAccent: '#10b981', // Bright emerald-500
        labelColor: 'text-purple-400 dark:text-purple-300' // Purple tone for Active Creators - improved contrast
      }
    ];
  }

  return [
    {
      key: 'newCreators',
      label: 'New Creators',
      icon: Users,
      gradient: ['var(--funnel-new-creators-start)', 'var(--funnel-new-creators-end)'],
      pulseRing: 'rgba(59, 130, 246, 0.18)',
      iconBg: 'rgba(59, 130, 246, 0.12)',
      iconAccent: '#3b82f6',
      labelColor: 'text-green-500 dark:text-green-400' // Green-ish tone for New Creators - improved contrast
    },
    {
      key: 'inSequence',
      label: 'In Sequence',
      icon: Mail,
      gradient: ['var(--funnel-in-sequence-start)', 'var(--funnel-in-sequence-end)'],
      pulseRing: 'rgba(245, 158, 11, 0.18)',
      iconBg: 'rgba(245, 158, 11, 0.12)',
      iconAccent: '#f59e0b',
      labelColor: 'text-blue-500 dark:text-blue-400' // Blue tone for In Sequence - improved contrast
    },
    {
      key: 'inNegotiation',
      label: 'In Negotiation',
      icon: Handshake,
      gradient: ['var(--funnel-in-negotiation-start)', 'var(--funnel-in-negotiation-end)'],
      pulseRing: 'rgba(249, 115, 22, 0.18)',
      iconBg: 'rgba(249, 115, 22, 0.12)',
      iconAccent: '#f97316',
      labelColor: 'text-amber-500 dark:text-amber-400' // Yellow/amber tone for In Negotiation - improved contrast
    },
    {
      key: 'activeCreators',
      label: 'Active Creators',
      icon: CheckCircle,
      gradient: ['var(--funnel-active-creators-start)', 'var(--funnel-active-creators-end)'],
      pulseRing: 'rgba(16, 185, 129, 0.2)',
      iconBg: 'rgba(16, 185, 129, 0.12)',
      iconAccent: '#10b981',
      labelColor: 'text-purple-500 dark:text-purple-400' // Purple tone for Active Creators - improved contrast
    }
  ];
};

interface FunnelSectionProps {
  bootData?: FunnelData; // Optional boot data for instant render
}

const FunnelSection: React.FC<FunnelSectionProps> = ({ bootData: initialBootData }) => {
  const { workspaceId, periodKey } = useSnapshotScope();
  const { canEditSection, isActuallyAdmin } = useUser();
  const isAdmin = isActuallyAdmin; // Check actual role, not effective role (viewer mode)
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => detectDarkTheme());
  const [pulsingCard, setPulsingCard] = useState<string | null>(null);
  const [hasAnimated, setHasAnimated] = useState<boolean>(false); // Track if initial animation has played
  const [iconRotation, setIconRotation] = useState<number>(0); // Track icon rotation for auto-rotation in viewer mode
  const [hoveredCard, setHoveredCard] = useState<string | null>(null); // Track which card is hovered for admin icon rotation
  const previousValuesRef = useRef<FunnelData | null>(null);
  const previousCanEditRef = useRef<boolean | null>(null); // Track previous canEdit to prevent pulse on mode switch
  const pulseTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const advanceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoRotateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const canEdit = canEditSection('funnel');
  
  // Check if current week, future week, or past week editing is allowed
  const currentWeekKey = getCurrentWeekKey();
  const currentWeekDate = getWeekStartDateFromKey(currentWeekKey);
  const periodDate = getWeekStartDateFromKey(periodKey);
  const isCurrentWeek = periodKey === currentWeekKey;
  const isFutureWeek = currentWeekDate && periodDate && periodDate.getTime() > currentWeekDate.getTime();
  const isPastWeek = currentWeekDate && periodDate && periodDate.getTime() < currentWeekDate.getTime();
  
  // Admins can always edit past weeks (for cumulative dashboard, mistake corrections, etc.)
  // Viewers can only edit current/future weeks (or past weeks if explicitly enabled)
  const isEditable = canEdit && (isCurrentWeek || isFutureWeek || (isPastWeek && (isAdmin || ALLOW_PAST_WEEK_EDITS)));
  
  // Mobile optimization: Device detection
  const deviceInfo = useMemo(() => {
    const userAgent = navigator.userAgent;
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const hardwareConcurrency = navigator.hardwareConcurrency || 4;
    const deviceMemory = navigator.deviceMemory || 4;
    const isLowEnd = isMobile && (hardwareConcurrency <= 2 || deviceMemory <= 2);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    return { isMobile, isLowEnd, prefersReducedMotion };
  }, []);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleMedia = (event: MediaQueryListEvent) => {
      // ✅ FIX: Use [data-theme] attribute instead of .theme-dark class (unified theme system)
      const rootIsDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setIsDarkMode(rootIsDark || event.matches);
    };

    if (media.addEventListener) {
      media.addEventListener('change', handleMedia);
    } else if (media.addListener) {
      media.addListener(handleMedia);
    }

    const observer = new MutationObserver(() => {
      // ✅ FIX: Use [data-theme] attribute instead of .theme-dark class (unified theme system)
      const rootIsDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setIsDarkMode(prev => (prev === rootIsDark ? prev : rootIsDark));
    });

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    if (document.body) {
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', handleMedia);
      } else if (media.removeListener) {
        media.removeListener(handleMedia);
      }
      observer.disconnect();
    };
  }, []);

  const funnelStages = useMemo(() => createFunnelStages(isDarkMode, !canEdit), [isDarkMode, canEdit]);
  
  // ✅ CRITICAL FIX: Always enable hook for save functionality
  // Boot data is just for initial render, but hook must be enabled for saves to work
  const { data: fetchedData, save } = useSimpleSnapshot<FunnelData>({
    workspaceId,
    section: 'funnel',
    periodKey,
    defaultValue: {
      newCreators: 0,
      inSequence: 0,
      inNegotiation: 0,
      activeCreators: 0
    },
    enabled: true // ✅ Always enabled - boot data is just for initial render
  });
  
  // Track if hook data has loaded (even if empty) to distinguish from "not loaded yet"
  const hasLoadedRef = useRef(false);
  
  // Track when fetched data loads (even if empty)
  useEffect(() => {
    if (fetchedData !== undefined) {
      hasLoadedRef.current = true;
    }
  }, [fetchedData]);
  
  // Sync boot data to hook state when provided (for instant render)
  // CRITICAL: Only sync if boot data is meaningful AND fetched data is empty/default
  // This prevents overwriting existing user data
  // ✅ QUICK WIN 4: Removed fetchedData from deps to prevent cascading re-renders
  useEffect(() => {
    if (initialBootData) {
      // Only sync if:
      // 1. Boot data is meaningful (not default/empty)
      // 2. Fetched data hasn't loaded yet (use hasLoadedRef to distinguish from empty)
      // ✅ BUG 1 FIX: Never use value-based checks for Funnel - check existence only
      // bootData is meaningful if it exists (regardless of values, including all zeros)
      const bootDataIsMeaningful = initialBootData !== undefined && initialBootData !== null;
      // fetchedData is empty only if it's undefined/null, not if it's all zeros
      const fetchedDataIsEmpty = !hasLoadedRef.current || !fetchedData;
      
      if (bootDataIsMeaningful && fetchedDataIsEmpty) {
        // Sync boot data to hook state for saves
        // Note: fetchedDataIsEmpty already ensures they're different, so no need for additional equality check
        save(initialBootData).catch((error) => {
          if (import.meta.env.DEV) {
            logger.error('Failed to sync boot data', { error }, 'FunnelSection');
          }
        });
      }
    }
  }, [initialBootData, save]); // ✅ Removed fetchedData from deps
  
  // Use boot data for initial render, but switch to fetchedData once it's available
  // This ensures immediate updates after save (fetchedData updates, but initialBootData doesn't)
  // CRITICAL: Once fetchedData is available and meaningful, prefer it over initialBootData
  const defaultData = {
    newCreators: 0,
    inSequence: 0,
    inNegotiation: 0,
    activeCreators: 0
  };
  
  // ✅ BUG 1 FIX: Never use value-based checks for Funnel - all zeros is valid data
  // Always prefer fetchedData if it exists (regardless of values)
  // Only fall back to initialBootData if fetchedData is undefined/null
  const funnelData = fetchedData !== undefined && fetchedData !== null
    ? fetchedData 
    : (initialBootData || defaultData);

  // Maintain a local shadow of the funnel data to reflect immediate edits without waiting for re-fetch
  const [localData, setLocalData] = useState<FunnelData | null>(null);

  // Keep localData in sync when fetchedData changes
  useEffect(() => {
    if (fetchedData) {
      setLocalData(fetchedData);
    }
  }, [fetchedData]);

  // ✅ NEW: Rehydrate localData from fetchedData when it updates after save
  // This ensures UI shows saved data immediately after remount or refresh
  useEffect(() => {
    if (fetchedData && !isEqual(fetchedData, localData)) {
      setLocalData(fetchedData);
    }
  }, [fetchedData, localData]);

  // ✅ BUG 1 FIX: Never use value-based checks - if localData exists, use it
  // This ensures all-zero edits are preserved in the UI
  const displayData: FunnelData = localData || funnelData;

  // Reset animation flag when periodKey changes (new week loaded)
  useEffect(() => {
    setHasAnimated(false);
  }, [periodKey]);

  // Mark as animated after animation completes (max delay is 400ms for 4th card + 500ms duration)
  useEffect(() => {
    if (!hasAnimated && displayData && !deviceInfo.prefersReducedMotion) {
      // Wait for all cards to animate (max delay is 400ms for 4th card + 500ms duration)
      const timer = setTimeout(() => {
        setHasAnimated(true);
      }, 1000); // Wait for all staggered animations to complete
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [hasAnimated, displayData, deviceInfo.prefersReducedMotion]);

  // Track value changes for pulse animation (only on actual value changes, not rerenders)
  useEffect(() => {
    // Skip pulse if canEdit changed (mode switch) - prevent false triggers
    const canEditChanged = previousCanEditRef.current !== null && previousCanEditRef.current !== canEdit;
    if (canEditChanged) {
      // Reset previousValuesRef to prevent false pulse on mode switch
      previousValuesRef.current = displayData;
      previousCanEditRef.current = canEdit;
      return;
    }
    
    // Skip on initial render (previousValuesRef is null)
    if (!previousValuesRef.current) {
      previousValuesRef.current = displayData;
      previousCanEditRef.current = canEdit;
      return;
    }

    // Only trigger pulse if values actually changed (not just rerender)
    const keys: Array<keyof FunnelData> = ['newCreators', 'inSequence', 'inNegotiation', 'activeCreators'];
    keys.forEach((key) => {
      const prevValue = previousValuesRef.current![key];
      const currValue = displayData[key];
      
      // Only pulse if value actually changed (not just rerender)
      if (prevValue !== currValue) {
        // Clear any existing timeout for this key
        const existingTimeout = pulseTimeoutRef.current.get(key);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }
        
        // Trigger pulse animation
        setPulsingCard(key);
        
        // Remove pulse class after animation completes
        const timeout = setTimeout(() => {
          setPulsingCard(null);
          pulseTimeoutRef.current.delete(key);
        }, 700);
        
        pulseTimeoutRef.current.set(key, timeout);
      }
    });
    
    // Update refs (doesn't trigger rerender)
    previousValuesRef.current = displayData;
    previousCanEditRef.current = canEdit;
  }, [displayData, canEdit]);

  // Auto-rotation for viewer mode: spin icons every couple seconds
  useEffect(() => {
    if (!canEdit && !deviceInfo.prefersReducedMotion) {
      // Rotate every 3 seconds for viewers
      autoRotateIntervalRef.current = setInterval(() => {
        setIconRotation(prev => prev + 360);
      }, 3000);
      
      return () => {
        if (autoRotateIntervalRef.current) {
          clearInterval(autoRotateIntervalRef.current);
        }
      };
    } else {
      // Clear interval if user becomes admin or prefers reduced motion
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
        autoRotateIntervalRef.current = null;
      }
      return undefined;
    }
  }, [canEdit, deviceInfo.prefersReducedMotion]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all pulse timeouts
      pulseTimeoutRef.current.forEach((timeout) => clearTimeout(timeout));
      pulseTimeoutRef.current.clear();
      
      // Clear advance timeout
      if (advanceTimeoutRef.current) {
        clearTimeout(advanceTimeoutRef.current);
      }
      
      // Clear auto-rotation interval
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
      }
    };
  }, []);

  // Input sanitization: Remove leading zeros
  const inputSanitize = (value: string): string => {
    // Remove leading zeros but keep single zero
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '0') return '0';
    // Remove leading zeros
    return trimmed.replace(/^0+/, '') || '0';
  };

  const handleEdit = (key: string, currentValue: number) => {
    if (!isEditable) return;
    setEditingKey(key);
    setEditValue(currentValue.toString());
  };

  const handleSave = (key: string, shouldAdvance: boolean = false) => {
    if (!isEditable) return;
    // Sanitize input to remove leading zeros
    const sanitizedValue = inputSanitize(editValue);
    const newValue = parseInt(sanitizedValue) || 0;
    // CRITICAL FIX: Use fetchedData instead of funnelData to ensure we're saving current state
    // funnelData might be using initialBootData which doesn't update, so we need to use fetchedData
    const currentData = fetchedData || {
      newCreators: 0,
      inSequence: 0,
      inNegotiation: 0,
      activeCreators: 0
    };
    const oldValue = currentData[key as keyof FunnelData];
    const updatedData = { ...currentData, [key]: newValue };
    // Update local state immediately for instant UI feedback
    setLocalData(updatedData);
    // Always save to ensure data persists
    // ✅ FIX: Add error handling to prevent unhandled promise rejections
    save(updatedData).catch((error) => {
      logger.error('Failed to save funnel data', { error, key, newValue, periodKey }, 'FunnelSection');
    });
    
    // Trigger pulse animation if value changed (only if not already pulsing)
    if (oldValue !== newValue && !pulsingCard) {
      // Clear any existing timeout for this key
      const existingTimeout = pulseTimeoutRef.current.get(key);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      
      setPulsingCard(key);
      const timeout = setTimeout(() => {
        setPulsingCard(null);
        pulseTimeoutRef.current.delete(key);
      }, 700);
      pulseTimeoutRef.current.set(key, timeout);
    }
    
    if (shouldAdvance) {
      // Find next input in sequence
      const currentIndex = funnelStages.findIndex(s => s.key === key);
      if (currentIndex < funnelStages.length - 1) {
        const nextKey = funnelStages[currentIndex + 1].key;
        const nextValue = updatedData[nextKey] || 0;
        // Small delay to ensure save completes and input is ready
        // Clear any existing advance timeout
        if (advanceTimeoutRef.current) {
          clearTimeout(advanceTimeoutRef.current);
        }
        advanceTimeoutRef.current = setTimeout(() => {
          handleEdit(nextKey, nextValue);
          advanceTimeoutRef.current = null;
        }, 50);
        return; // Don't clear editing key yet
      }
    }
    
    setEditingKey(null);
  };

  const handleCancel = () => {
    if (editingKey) {
      // Revert to committed value (from fetchedData, not localData)
      const currentData = fetchedData || {
        newCreators: 0,
        inSequence: 0,
        inNegotiation: 0,
        activeCreators: 0
      };
      const committedValue = currentData[editingKey as keyof FunnelData] || 0;
      // Revert localData if it was changed
      if (localData && localData[editingKey as keyof FunnelData] !== committedValue) {
        setLocalData({ ...localData, [editingKey]: committedValue });
      }
    }
    setEditingKey(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, key: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Enter-to-advance: save current and move to next, or blur if last
      const currentIndex = funnelStages.findIndex(s => s.key === key);
      const isLast = currentIndex === funnelStages.length - 1;
      handleSave(key, !isLast); // Advance if not last
      if (isLast) {
        // Last input: blur to exit edit mode
        e.currentTarget.blur();
      }
    } else if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Tab' && !e.shiftKey) {
      // Tab to next stat
      e.preventDefault();
      const currentIndex = funnelStages.findIndex(s => s.key === key);
      if (currentIndex < funnelStages.length - 1) {
        handleSave(key, true);
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      // Shift+Tab to previous stat
      e.preventDefault();
      const currentIndex = funnelStages.findIndex(s => s.key === key);
      if (currentIndex > 0) {
        const prevKey = funnelStages[currentIndex - 1].key;
        const prevValue = displayData[prevKey] || 0;
        handleSave(key);
        // Clear any existing advance timeout
        if (advanceTimeoutRef.current) {
          clearTimeout(advanceTimeoutRef.current);
        }
        advanceTimeoutRef.current = setTimeout(() => {
          handleEdit(prevKey, prevValue);
          advanceTimeoutRef.current = null;
        }, 50);
      }
    }
  };

  const handleBlur = (key: string) => {
    // Sanitize on blur to remove leading zeros
    const sanitized = inputSanitize(editValue);
    setEditValue(sanitized);
    // ✅ FIX: Update localData immediately on blur (before save) so UI reflects cleared values
    const newValue = parseInt(sanitized) || 0;
    const currentData = fetchedData || {
      newCreators: 0,
      inSequence: 0,
      inNegotiation: 0,
      activeCreators: 0
    };
    const oldValue = currentData[key as keyof FunnelData];
    const updatedData = { ...currentData, [key]: newValue };
    setLocalData(updatedData); // Update display immediately
    
    // Only save if value actually changed (prevents unnecessary saves and pulses)
    if (oldValue !== newValue) {
      handleSave(key);
    } else {
      // Value didn't change, just exit edit mode
      setEditingKey(null);
    }
  };

  // Helper to convert hex to rgba (moved outside map to avoid recreation)
  const hexToRgba = useCallback((hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }, []);

  return (
    <SectionContainer 
      variant="bare"
      testId="section-funnel"
      aria-label="Funnel section"
    >
      <SectionHeader 
        title="Funnel" 
        href="https://docs.google.com/funnel-master"
      />
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 mt-6 gap-4 md:gap-6" style={{ gap: 'clamp(1rem, 2vw, 1.5rem)' }}>
        {funnelStages.map((stage, index) => {
          const Icon = stage.icon;
          const value = (displayData[stage.key] as number) || 0;
          const isEditing = editingKey === stage.key;
          const isPulsing = pulsingCard === stage.key;
          
          // Stagger delay for smooth slide-in animation (respects prefers-reduced-motion)
          const staggerDelay = deviceInfo.prefersReducedMotion ? 0 : Math.min(index * 100, 400);
          
          // Semantic health indicators
          // Warning: In Negotiation > Active Creators (indicates bottleneck)
          const inNegotiation = displayData.inNegotiation || 0;
          const activeCreators = displayData.activeCreators || 0;
          const hasWarning = inNegotiation > activeCreators && inNegotiation > 0; // Only show if meaningful
          
          // Extract color from iconAccent for radial glow (stage-specific color)
          const glowColor = stage.iconAccent;
          // Vibrant glow for dark mode (both admin and viewer)
          const glowOpacity = isDarkMode ? 0.40 : 0.35;

          // Dark mode: Add vibrant edge glow (both admin and viewer)
          // Use proper rgba() format instead of hex shorthand for browser compatibility
          const darkModeEdgeGlow = isDarkMode 
            ? `0 0 0 1px ${hexToRgba(stage.iconAccent, 0.40)}, 0 0 20px ${hexToRgba(stage.iconAccent, 0.30)}, 0 0 40px ${hexToRgba(stage.iconAccent, 0.15)}`
            : '';

          // Base state: "Breathing Glass" - subtle ambient glow (no hover effects)
          // Dark mode: Add subtle stage-specific edge glow for visual appeal
          const baseShadow = isDarkMode
            ? `0 0 0 1px rgba(255,255,255,0.06), 0 8px 30px rgba(0,0,0,0.35), ${darkModeEdgeGlow}`
            : '0 0 0 1px rgba(0,0,0,0.04), 0 8px 30px rgba(15, 23, 42, 0.08)';
          
          // Warning state: subtle amber edge glow
          const warningShadow = hasWarning
            ? isDarkMode
              ? 'inset 0 0 0 1px rgba(255,180,60,0.35), 0 0 18px rgba(255,180,60,0.25), 0 8px 30px rgba(0,0,0,0.35)'
              : 'inset 0 0 0 1px rgba(245,158,11,0.35), 0 0 18px rgba(245,158,11,0.20), 0 8px 30px rgba(15, 23, 42, 0.08)'
            : baseShadow;

          // Gradient overlay for semantic color coding (static in viewer mode)
          const gradientBackground = isDarkMode
            ? `linear-gradient(135deg, ${stage.gradient[0]} 0%, ${stage.gradient[1]} 100%)`
            : `linear-gradient(135deg, color-mix(in srgb, ${stage.gradient[0]} 25%, var(--bg-elev) 75%) 0%, color-mix(in srgb, ${stage.gradient[1]} 25%, var(--bg-elev) 75%) 100%)`;
          
          // Static overlay opacity (no hover changes)
          // Match admin styling: no gradient overlay for cleaner look
          const overlayOpacity = 0;

          // Card animation: static base, pulse on change only (no hover effects)
          // Respect prefers-reduced-motion: disable pulse scale
          const shouldAnimate = !deviceInfo.prefersReducedMotion;
          
          // Initial slide-in animation (only on first load per week, respects prefers-reduced-motion)
          // Use a key based on periodKey to force re-animation when week changes
          const shouldShowInitialAnimation = shouldAnimate && !hasAnimated;
          
          const cardInitial = shouldShowInitialAnimation ? {
            opacity: 0,
            y: 20,
            scale: 0.95
          } : false; // Use false to skip initial animation if already shown
          
          const cardAnimate = {
            opacity: 1,
            y: 0,
            scale: shouldAnimate && isPulsing ? 1.02 : 1,
            boxShadow: hasWarning ? warningShadow : baseShadow
          };

          const cardTransition = {
            duration: shouldAnimate ? (isPulsing ? 0.7 : (shouldShowInitialAnimation ? 0.5 : 0)) : 0,
            delay: shouldShowInitialAnimation ? staggerDelay / 1000 : 0,
            ease: 'easeOut'
          };

          return (
            <div
              key={`${periodKey}-${stage.key}`}
              onClick={() => isEditable && !isEditing && handleEdit(stage.key, value)}
              onKeyDown={(e) => {
                // Keyboard equivalent for click (Enter or Space)
                if (isEditable && !isEditing && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleEdit(stage.key, value);
                }
              }}
              tabIndex={isEditable ? 0 : -1}
              className={`relative group ${isEditable ? 'cursor-pointer focus:outline-none' : ''}`}
            >
              <motion.div
                className={`relative funnel-card rounded-xl p-7 overflow-hidden bg-elev border border-border transition-transform duration-200 ${isPulsing && !deviceInfo.prefersReducedMotion ? 'pulse' : ''}`}
                initial={cardInitial}
                animate={cardAnimate}
                transition={cardTransition as Transition}
                onHoverStart={canEdit ? () => setHoveredCard(stage.key) : undefined}
                onHoverEnd={canEdit ? () => setHoveredCard(null) : undefined}
                style={{ 
                  backgroundColor: 'var(--bg-elev)',
                  borderWidth: '0.5px',
                  borderRadius: '0.75rem',
                  // Stage-specific radial glow color
                  '--funnel-glow-color': glowColor,
                  '--funnel-glow-opacity': glowOpacity
                } as React.CSSProperties}
              >
                {/* Semantic color overlay - static, vibrant for dark mode */}
                <div
                  className="absolute inset-0 z-0 rounded-xl overflow-hidden"
                  style={{ background: gradientBackground, opacity: overlayOpacity }}
                />

                <div className="relative z-10 flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className={`funnel-card-label text-sm font-semibold uppercase tracking-wide mb-2 ${stage.labelColor}`}>
                      {stage.label}
                    </p>
                    {isEditing ? (
                      <div className="space-y-1">
                        <input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, stage.key)}
                          onBlur={() => handleBlur(stage.key)}
                          autoFocus
                          className="text-3xl md:text-4xl font-bold text-textPrimary bg-transparent border-b-2 border-accent outline-none w-24 transition-all duration-200 hover:border-accentStrong focus:border-accentStrong focus:ring-2 focus:ring-accent focus:ring-opacity-20 rounded-sm px-1"
                          aria-label={`Edit ${stage.label.toLowerCase()}`}
                        />
                        <p className="text-xs text-textMuted">
                          Press Enter to save and continue, Esc to cancel
                        </p>
                      </div>
                    ) : (
                      <h3 
                        className="funnel-card-value text-3xl md:text-4xl font-bold relative z-10"
                        style={{
                          filter: !deviceInfo.prefersReducedMotion && isPulsing ? 'brightness(1.15)' : 'brightness(1)',
                          transition: !deviceInfo.prefersReducedMotion && isPulsing ? 'filter 0.7s ease-out' : 'filter 0.2s ease-out',
                          // Dark mode: Add vibrant text glow (both admin and viewer)
                          // Use proper rgba() format for browser compatibility
                          textShadow: isDarkMode 
                            ? `0 0 10px ${hexToRgba(stage.iconAccent, 0.24)}, 0 0 4px ${hexToRgba(stage.iconAccent, 0.16)}`
                            : 'none'
                        }}
                      >
                        <AnimatedCounter value={value} />
                      </h3>
                    )}
                  </div>
                  
                  <motion.div
                    className="p-3 rounded-xl relative z-10"
                    style={{ 
                      backgroundColor: stage.iconBg,
                      // Dark mode: Add vibrant icon glow (both admin and viewer)
                      // Use proper rgba() format for browser compatibility
                      boxShadow: isDarkMode 
                        ? `0 0 12px ${hexToRgba(stage.iconAccent, 0.28)}, 0 0 4px ${hexToRgba(stage.iconAccent, 0.18)}`
                        : 'none'
                    }}
                    animate={{
                      scale: !deviceInfo.prefersReducedMotion && isPulsing ? 1.05 : 1,
                      // Admin: rotate on card hover, Viewer: auto-rotation every couple seconds
                      rotate: canEdit 
                        ? (hoveredCard === stage.key && !deviceInfo.prefersReducedMotion ? 360 : 0)
                        : (!deviceInfo.prefersReducedMotion ? iconRotation : 0)
                    }}
                    transition={{ 
                      duration: !deviceInfo.prefersReducedMotion && isPulsing 
                        ? 0.7 
                        : (canEdit && hoveredCard === stage.key 
                          ? 0.6 
                          : (canEdit ? 0.3 : 1.2)),
                      ease: canEdit ? 'easeInOut' : 'easeOut'
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: stage.iconAccent }} />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          );
        })}
      </div>
    </SectionContainer>
  );
};

export default FunnelSection;
